/**
 * Configuration Module — React Query key factory
 *
 * Mirrors the Tickets module's `ticketKeys` convention (see
 * features/tickets/services/ticket.service.ts), adapted for Configuration's
 * shape: several independent master-data entities instead of one, so each
 * entity gets its own namespace under a shared `all` root.
 */
export const configurationKeys = {
  all: ['configuration'] as const,

  departments: {
    all: () => [...configurationKeys.all, 'departments'] as const,
    lists: () => [...configurationKeys.departments.all(), 'list'] as const,
    list: (tenantId: string) => [...configurationKeys.departments.lists(), tenantId] as const,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    paged: (params: any) => [...configurationKeys.departments.lists(), 'paged', params] as const,
  },

  designations: {
    all: () => [...configurationKeys.all, 'designations'] as const,
    lists: () => [...configurationKeys.designations.all(), 'list'] as const,
    list: (tenantId: string) => [...configurationKeys.designations.lists(), tenantId] as const,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    paged: (params: any) => [...configurationKeys.designations.lists(), 'paged', params] as const,
  },

  roles: {
    all: () => [...configurationKeys.all, 'roles'] as const,
    lists: () => [...configurationKeys.roles.all(), 'list'] as const,
    list: (tenantId: string) => [...configurationKeys.roles.lists(), tenantId] as const,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    paged: (params: any) => [...configurationKeys.roles.lists(), 'paged', params] as const,
  },

  shifts: {
    all: () => [...configurationKeys.all, 'shifts'] as const,
    lists: () => [...configurationKeys.shifts.all(), 'list'] as const,
    list: (tenantId: string) => [...configurationKeys.shifts.lists(), tenantId] as const,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    paged: (params: any) => [...configurationKeys.shifts.lists(), 'paged', params] as const,
  },

  locations: {
    all: () => [...configurationKeys.all, 'locations'] as const,
    lists: () => [...configurationKeys.locations.all(), 'list'] as const,
    list: (tenantId: string) => [...configurationKeys.locations.lists(), tenantId] as const,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    paged: (params: any) => [...configurationKeys.locations.lists(), 'paged', params] as const,
  },

  holidays: {
    all: () => [...configurationKeys.all, 'holidays'] as const,
    lists: () => [...configurationKeys.holidays.all(), 'list'] as const,
    list: (year: number) => [...configurationKeys.holidays.lists(), year] as const,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    paged: (params: any) => [...configurationKeys.holidays.lists(), 'paged', params] as const,
  },

  leaveTypes: {
    all: () => [...configurationKeys.all, 'leaveTypes'] as const,
    lists: () => [...configurationKeys.leaveTypes.all(), 'list'] as const,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    paged: (params: any) => [...configurationKeys.leaveTypes.lists(), 'paged', params] as const,
  },

  permissions: {
    all: () => [...configurationKeys.all, 'permissions'] as const,
    catalog: () => [...configurationKeys.permissions.all(), 'catalog'] as const,
    roleMatrix: (roleId: string, tenantId: string) =>
      [...configurationKeys.permissions.all(), 'roleMatrix', roleId, tenantId] as const,
    userMatrix: (userId: string, tenantId: string) =>
      [...configurationKeys.permissions.all(), 'userMatrix', userId, tenantId] as const,
    users: (tenantId: string) => [...configurationKeys.permissions.all(), 'users', tenantId] as const,
  },

  probation: {
    all: () => [...configurationKeys.all, 'probation'] as const,
    config: () => [...configurationKeys.probation.all(), 'config'] as const,
  },
} as const;
